import { useState } from "react";
import { useParams } from "react-router-dom";
import { Flex, Form, Typography } from "antd";
import { useNotification, useTranslate } from "@refinedev/core";
import { PlusIcon } from "@phosphor-icons/react";
import { Skeleton, type QuickRecallCard } from "@libertify/ui";
import {
  useQuickRecall,
  useSaveQuickRecall,
} from "@libertify/experience-viewer";
import { API_URL } from "@/constants";
import { useAuth } from "hooks/useAuth";
import { PlaybookAddOnCard } from "../../PlaybookAddOnCard";
import { DashedAddButton } from "../../DashedAddButton";
import { CONFIG_ITEM } from "../../../data/addons";
import type { QuickRecallCardRow, QuickRecallCardsFormValues } from "../types";
import { useAssetListAutosave } from "../hooks/useAssetListAutosave";
import { QuickRecallCardItem } from "./QuickRecallCardItem";

const KEY = "experienceEdit.quickRecallCard";

/** Session-only row ids: unique for the life of the page is all they need. */
let nextRowId = 0;
const newRowId = (): string => `card-${++nextRowId}`;
const toRow = (card: QuickRecallCard): QuickRecallCardRow => ({
  ...card,
  id: newRowId(),
});

/** A card with text on either side. */
const hasCardContent = (card: Partial<QuickRecallCard>): boolean =>
  Boolean(card.front?.trim() || card.back?.trim());

/** The rows as the deck is persisted: the wire shape, ids stripped. */
const toDeck = (rows: Partial<QuickRecallCardRow>[]): QuickRecallCard[] =>
  rows.map((row) => ({ front: row.front ?? "", back: row.back ?? "" }));

const sameDeck = (a: QuickRecallCard[], b: QuickRecallCard[]): boolean =>
  a.length === b.length &&
  a.every((card, i) => {
    const other = b.at(i);
    return card.front === other?.front && card.back === other?.back;
  });

export interface QuickRecallEditCardProps {
  /** Whether the deck is shown to viewers of the experience. */
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  visiblePending?: boolean;
}

/**
 * Quick recall card for the experience edit page: the generated deck as one
 * editable term/definition pair per card, each removable, plus "Add a card".
 *
 * Saves like the Chat and Podcast edit cards — every change, debounced,
 * nothing to press. The deck lives in its own asset and there is no per-card
 * endpoint, so each save replaces the whole deck (see `useSaveQuickRecall`).
 * A saved deck is not what viewers see: the publication snapshots its assets,
 * and the backend marks it out of date on every asset PATCH (unlike the
 * settings endpoint the Chat and Podcast cards write to, which is why those
 * flag it themselves). The user republishes ("Update" in the out-of-date
 * widget) when the deck is ready.
 */
export const QuickRecallEditCard = ({
  visible,
  onVisibleChange,
  visiblePending,
}: QuickRecallEditCardProps) => {
  const t = useTranslate();
  const { projectId } = useParams<{ projectId: string }>();
  // Same bearer the experience page hands the viewer's add-on reads.
  const { accessToken } = useAuth();
  const { cards, assetId, isLoading, isError } = useQuickRecall({
    apiBaseUrl: API_URL,
    projectId: projectId ?? "",
    token: accessToken,
  });

  const renderBody = () => {
    if (isLoading) return <Skeleton variant="text" rows={6} />;
    if (isError) {
      return (
        <Typography.Text type="danger">{t(`${KEY}.error`)}</Typography.Text>
      );
    }
    // No asset yet means generation hasn't produced the deck — there is
    // nothing to write into, so no editor either.
    if (!assetId) {
      return (
        <Typography.Text type="secondary">
          {t(`${KEY}.notGenerated`)}
        </Typography.Text>
      );
    }
    return (
      <QuickRecallDeckForm
        loaded={cards}
        assetId={assetId}
        projectId={projectId ?? ""}
        accessToken={accessToken}
      />
    );
  };

  return (
    <PlaybookAddOnCard
      itemId={CONFIG_ITEM.Flashcards}
      title={t(`${KEY}.title`)}
      description={t(`${KEY}.subtitle`)}
      visible={visible}
      visiblePending={visiblePending}
      onVisibleChange={onVisibleChange}
    >
      {renderBody()}
    </PlaybookAddOnCard>
  );
};

interface QuickRecallDeckFormProps {
  loaded: QuickRecallCard[];
  assetId: string;
  projectId: string;
  accessToken: string | null | undefined;
}

/**
 * Mounted once the deck is in: antd reads `initialValues` only at mount. Owns
 * the autosave — debounced, flushed on unmount (the card unmounts on every
 * rail switch), reverted on failure — mirroring `PodcastEditCard`.
 *
 * Rows carry a session id (see `QuickRecallCardRow`) so the save can tell a
 * freshly added blank row, which is simply left out, from an existing card
 * whose text was cleared to be retyped, which holds the save instead of being
 * deleted.
 */
const QuickRecallDeckForm = ({
  loaded,
  assetId,
  projectId,
  accessToken,
}: QuickRecallDeckFormProps) => {
  const t = useTranslate();
  const { open } = useNotification();
  const [form] = Form.useForm<QuickRecallCardsFormValues>();
  const { save } = useSaveQuickRecall({
    apiBaseUrl: API_URL,
    projectId,
    token: accessToken,
  });

  // Ids are minted once, at mount — the loaded deck may be re-read later, and
  // the form owns the rows from here on.
  const [initialRows] = useState<QuickRecallCardRow[]>(() => loaded.map(toRow));
  const persist = useAssetListAutosave<QuickRecallCardRow, QuickRecallCard>({
    initialRows,
    saveKey: assetId,
    // An existing card cleared to be retyped is an edit in progress, not a
    // removal, so it holds its last saved content until text comes back —
    // leaving it out is what would delete it. The cards around it save
    // normally meanwhile; holding them back loses their edits silently.
    // A card that was never saved is not content until someone types into it;
    // unlike a quiz question, half of one is still usable.
    resolveRow: (row, previous) => {
      if (previous) {
        return hasCardContent(row) ? (row as QuickRecallCardRow) : previous;
      }
      return hasCardContent(row) ? (row as QuickRecallCardRow) : null;
    },
    toWire: toDeck,
    isSameWire: sameDeck,
    save: (cards) => save(assetId, cards),
    onSaveFailed: () =>
      open?.({ type: "error", message: t(`${KEY}.saveFailed`) }),
    revert: (cards) => form.setFieldsValue({ cards }),
  });

  return (
    <Form<QuickRecallCardsFormValues>
      form={form}
      layout="vertical"
      initialValues={{ cards: initialRows }}
      onValuesChange={(_, values) => persist(values.cards ?? [])}
    >
      <Form.List name="cards">
        {(fields, { add, remove }) => (
          <Flex vertical gap={16}>
            {fields.length === 0 ? (
              <Typography.Text type="secondary">
                {t(`${KEY}.empty`)}
              </Typography.Text>
            ) : null}
            {fields.map((field, index) => (
              <QuickRecallCardItem
                key={field.key}
                name={field.name}
                position={index + 1}
                onRemove={() => remove(field.name)}
              />
            ))}
            <DashedAddButton
              icon={<PlusIcon size={16} weight="bold" />}
              label={t(`${KEY}.add`)}
              onClick={() =>
                add({
                  id: newRowId(),
                  front: "",
                  back: "",
                } satisfies QuickRecallCardRow)
              }
              iconBordered={false}
              centered
            />
          </Flex>
        )}
      </Form.List>
    </Form>
  );
};

export default QuickRecallEditCard;
